import { tokenize, translateTokensToR1C1, stringifyTokens, type Token } from '@borgar/fx/xlsx';
import { Element } from '@borgar/simple-xml';
import { toInt, toNum } from '../utils/typecast.ts';
import { attr, boolAttr, numAttr } from '../utils/attr.ts';
import { unescape } from '../utils/unescape.ts';
import { RelativeFormula } from '../RelativeFormula.ts';
import { normalizeFormula, normalizeFormulaTokens } from '../utils/normalizeFormula.ts';
import { ConversionContext } from '../ConversionContext.ts';
import type { Cell, DataTable } from '@jsfkit/types';
import { dateToSerial } from '../utils/dateToSerial.ts';
import { UnsupportedError } from '../errors.ts';
import { ERROR_NAMES } from '../constants.ts';
import { getFirstChild } from '../utils/getFirstChild.ts';
import { parseTimeToSerial } from '../utils/parseTimeToSerial.ts';

export const relevantStyle = (obj?: Record<string, any>): boolean => {
  if (!obj) {
    return false;
  }
  return !!(
    // obj['number-format'] ||
    obj.fillColor ||
    obj.patternColor ||
    obj.patternStyle ||
    obj.borderTopStyle ||
    obj.borderLeftStyle ||
    obj.borderBottomStyle ||
    obj.borderRightStyle
  );
};

function prepFormula (formula: string | RelativeFormula, cellId: string, context: ConversionContext) {
  if (formula) {
    if (context.options.cellFormulas) {
      if (typeof formula === 'string') {
        return normalizeFormula(formula, context);
      }
      return normalizeFormula(formula.translate(cellId), context);
    }
    else {
      let tokens: Token[];
      if (typeof formula === 'string') {
        tokens = tokenize(formula, { allowTernary: true });
        tokens = translateTokensToR1C1(tokens, cellId);
        tokens = normalizeFormulaTokens(tokens, context, true);
      }
      else {
        tokens = formula.getR1C1Tokens();
        tokens = normalizeFormulaTokens(tokens, context, true);
      }
      const rc = stringifyTokens(tokens);
      return context._formulasR1C1.add(rc);
    }
  }
}

// ECMA - 18.3.1.4 (Cell)
export function handlerCell (node: Element, address: string, context: ConversionContext): Cell | undefined {
  const cell: Cell = {};
  // FIXME: these props are scoped by the sheet but exist on the WB object
  //        during processing and are wiped per-sheet
  const sharedF = context._shared!;

  // .t = data type: The possible values for this attribute are defined by the
  //                 ST_CellType simple type (§18.18.11).
  let valueType = attr(node, 't', 'n');

  // .s = style index: The index of this cell's style.
  //                   Style records are stored in the Styles Part.
  const styleIndex = Math.trunc(numAttr(node, 's', 0));
  if (styleIndex) {
    cell.s = styleIndex;
  }

  const vNode = getFirstChild(node, 'v');
  let v = vNode ? vNode.textContent : null;

  // .vm = value metadata index: The zero-based index of the value metadata
  //                             record associated with this cell's value
  const vm = numAttr(node, 'vm');
  if (vm && context.metadata) {
    const meta = context.metadata.values[vm - 1];
    if (meta._type === '_error') {
      valueType = 'e';
      // TODO: some of these may have .subType, does is matter?
      if (meta.errorType === 8) {
        v = '#SPILL!';
      }
      else if (meta.errorType === 11) {
        v = '#UNKNOWN!';
      }
      else if (meta.errorType === 12) {
        v = '#FIELD!';
      }
      else if (meta.errorType === 13) {
        v = '#CALC!';
      }
    }
  }

  if (valueType === 'inlineStr') {
    valueType = 'str';
    v = node.querySelectorAll('is t').map(d => d.textContent).join('');
  }

  // ECMA - 18.3.1.40 f (Formula)
  const fNode = getFirstChild(node, 'f');
  if (v || valueType === 'str') {
    if (valueType === 's') {
      cell.v = context.sst && v != null ? context.sst[toInt(v)] : '';
    }
    else if (valueType === 'str') {
      // Excel marks formula errors with `t="e"`, but Google Sheets uses
      // `t="str"` with an error-looking cached value (e.g. "#NAME?"). For
      // Google Sheets exports we treat these as errors. For other sources,
      // `t="str"` means the formula genuinely evaluated to a string — even
      // one that looks like an error name (e.g. `=A1` where A1 is "#VALUE!").
      if (context.isLikelyGSExport && fNode && v && ERROR_NAMES.includes(v)) {
        // valueType = 'e';
        cell.t = 'e';
        cell.v = v;
      }
      else {
        // valueType = 's';
        cell.v = v || '';
      }
    }
    else if (valueType === 'b') {
      cell.v = !!toInt(v);
    }
    else if (valueType === 'e') {
      // FIXME: ensure error is a known error!
      cell.v = v;
      cell.t = 'e';
    }
    else if (valueType === 'd') {
      // cell.t = 'd';
      if (v) {
        if (!/[T ]/i.test(v) && v.includes(':')) {
          cell.v = parseTimeToSerial(v);
        }
        else {
          const serialDate = dateToSerial(new Date(Date.parse(v)));
          if (serialDate != null) {
            cell.v = serialDate + (
              // adjust dates if the workbook uses 1904 data system
              context.workbook?.calculationProperties?.epoch === 1904 ? -1462 : 0
            );
          }
        }
      }
    }
    else if (valueType === 'n') {
      const val = toNum(v);
      cell.v = val;
    }
    else {
      throw new UnsupportedError('Missing support for data type: ' + valueType);
    }
  }

  if (fNode) {
    // .t (Formula Type): [ array | dataTable | normal | shared ]
    const formulaType = attr(fNode, 't', 'normal');
    // array for array-formula
    if (formulaType === 'array') {
      // .ref (Range of Cells): Range of cells which the formula applies to.
      //   Only required for shared formula, array formula or data table.
      //   Only written on the master formula, not subsequent formulas belonging
      //   to the same shared group, array, or data table.
      //   The possible values for this attribute are defined by the
      //   ST_Ref simple type (§18.18.62).
      const cellsRange = attr(fNode, 'ref');
      if (cellsRange) {
        cell.F = cellsRange;
        context._arrayFormula!.push(cellsRange);
      }
      // cm="1" on the cell element indicates a dynamic array formula
      // (referencing XLDAPR metadata). Its absence on an array formula
      // means CSE (Ctrl+Shift+Enter). Preserving this distinction lets
      // jsf2xlsx emit cm="1" only on dynamic arrays, not on CSE formulas.
      if (!numAttr(node, 'cm')) {
        cell.cse = true;
      }
      cell.f = prepFormula(fNode.textContent, address, context);
    }
    // shared for shared formula
    else if (formulaType === 'shared') {
      // .si (Shared Group Index) - Optional attribute to optimize load
      //       performance by sharing formulas. the si attribute is used to
      //       refer to the cell containing the formula. Two formulas are
      //       considered to be the same when their respective representations
      //       in R1C1-reference notation, are the same.
      const shareGroupIndex = numAttr(fNode, 'si');
      if (shareGroupIndex != null) {
        if (!sharedF.has(shareGroupIndex)) {
          const relF = new RelativeFormula(fNode.textContent, address);
          sharedF.set(shareGroupIndex, relF);
          cell.f = prepFormula(relF, address, context);
        }
        else {
          cell.f = prepFormula(sharedF.get(shareGroupIndex)!, address, context);
        }
      }
    }
    // dataTable for data table formula
    else if (formulaType.toLowerCase() === 'datatable') {
      const ref = attr(fNode, 'ref');
      const r1 = attr(fNode, 'r1');
      if (ref && r1) {
        const dt: DataTable = { ref, r1 };
        const dtr = boolAttr(fNode, 'dtr', false);
        const dt2D = boolAttr(fNode, 'dt2D', false);
        const r2 = attr(fNode, 'r2');
        if (dtr) {
          dt.dtr = true;
        }
        if (dt2D) {
          dt.dt2D = true;
        }
        if (r2) {
          dt.r2 = r2;
        }
        cell.dt = dt;
      }
    }
    else {
      cell.f = prepFormula(fNode.textContent, address, context);
    }
  }

  // unescape the strange OOXML character escaping
  // (seems only used for <32 ASCII codes?)
  if (typeof cell.v === 'string') {
    cell.v = unescape(cell.v);
  }

  // don't emit the cell if it is empty
  if (
    cell.v == null &&
    cell.f == null &&
    cell.dt == null &&
    (!cell.s || (context.options.skipStyledEmptyCells && !relevantStyle(context.workbook!.styles?.[cell.s])))
  ) {
    return;
  }

  return cell;
}
